/**
 * Product page scroll behavior:
 * 1. Activates scroll-converter so vertical wheel/trackpad input scrolls the
 *    document horizontally through the image strip (desktop only).
 * 2. Keeps the handwritten description image aligned to the gap between the
 *    first two product images, then clamps it so a portion always peeks as
 *    the page scrolls past — from the left under the nav on desktop (X),
 *    from the top under the nav on mobile (Y). The note stays inside the
 *    image strip: on mobile it cannot cross the purchase sheet, and once
 *    that sheet leaves the bottom edge the note is parked in the strip and
 *    scrolls away with the photos. On desktop it cannot cross into related
 *    products.
 * 3. Lets the handwritten image be dragged around the viewport (mouse +
 *    touch). The dropped spot becomes the new follow origin, so peek still
 *    applies on scroll. A click or tap without dragging scrolls back to the
 *    start of the page, then eases the image back to its original origin
 *    once scrolling has stopped.
 *
 * Depends on window.scrollConverter from scroll-converter.min.js.
 */
(function () {
  var mobileQuery = window.matchMedia(
    "(max-width: 749px), (hover: none) and (pointer: coarse)",
  );
  var narrowQuery = window.matchMedia("(max-width: 749px)");

  /* --- scroll converter -------------------------------------------------- */

  function syncScrollConverter() {
    if (!window.scrollConverter) return;

    if (mobileQuery.matches) {
      window.scrollConverter.deactivate();
      return;
    }

    window.scrollConverter.activate();
  }

  /* --- handwritten peek -------------------------------------------------- */

  var PEEK_REM = 8;
  var DRAG_THRESHOLD_PX = 8;
  var RESET_MS = 450;
  var peekFrame = 0;
  var isDragging = false;
  var isResetting = false;
  var pendingRestore = false;
  var dragMoved = false;
  var activePointerId = null;
  var dragStartClientX = 0;
  var dragStartClientY = 0;
  var dragStartLeft = 0;
  var dragStartTop = 0;
  var offsetX = 0;
  var offsetY = 0;
  var restoreScrollTimer = 0;
  var restoreTimer = 0;
  var restoreFallbackTimer = 0;
  var restoreFinish = null;

  function handwrittenEls() {
    return {
      anchor: document.querySelector("[data-handwritten-anchor]"),
      image: document.querySelector("[data-handwritten-description-image]"),
    };
  }

  function remPx(rems) {
    var rootSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    return rems * (rootSize || 16);
  }

  function peekPx() {
    return remPx(PEEK_REM);
  }

  function navOffsetPx() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue(
      "--spacing-nav-height",
    );
    var parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 80;
  }

  function clearHandwrittenInline(image) {
    image.removeAttribute("data-positioned");
    image.removeAttribute("data-dragging");
    image.removeAttribute("data-resetting");
    image.removeAttribute("data-parked");
    image.style.left = "";
    image.style.right = "";
    image.style.top = "";
    image.style.position = "";
  }

  function applyImagePos(image, left, top) {
    image.style.right = "";
    image.style.left = left + "px";
    image.style.top = top + "px";
    image.setAttribute("data-positioned", "");
  }

  function clampToPage(left, top, width, height) {
    var peek = peekPx();
    var minLeft = peek - width;
    var maxLeft = window.innerWidth - peek;
    var minTop = peek - height;
    var maxTop = window.innerHeight - peek;

    if (minLeft > maxLeft) {
      left = (window.innerWidth - width) / 2;
    } else {
      left = Math.min(Math.max(left, minLeft), maxLeft);
    }

    if (minTop > maxTop) {
      top = (window.innerHeight - height) / 2;
    } else {
      top = Math.min(Math.max(top, minTop), maxTop);
    }

    return { left: left, top: top };
  }

  function unconstrainedPos(els, width, height) {
    var anchorRect = els.anchor.getBoundingClientRect();

    if (narrowQuery.matches) {
      return {
        left: window.innerWidth - width / 2,
        top: anchorRect.top - height / 2,
      };
    }

    return {
      left: anchorRect.left - width / 2,
      top: navOffsetPx(),
    };
  }

  function imageStrip(image) {
    return image.closest("[data-product-images]");
  }

  function purchaseSheet() {
    return document.querySelector("[data-purchase-sheet]");
  }

  /* Sticky bottom: pinned while the sheet's edge is on the viewport bottom. */
  function purchaseStackPinned() {
    var sheet = purchaseSheet();
    if (!sheet) return true;
    return sheet.getBoundingClientRect().bottom >= window.innerHeight - 2;
  }

  function shouldPark(els) {
    var strip = imageStrip(els.image);
    if (!strip) return false;

    if (narrowQuery.matches) return !purchaseStackPinned();

    var rect = strip.getBoundingClientRect();
    return rect.right <= 0 || rect.left >= window.innerWidth;
  }

  function clampToImageArea(els, left, top, width, height) {
    var pos = clampToPage(left, top, width, height);
    var strip = imageStrip(els.image);
    if (!strip) return pos;

    var stripRect = strip.getBoundingClientRect();

    if (narrowQuery.matches) {
      var limitBottom = stripRect.bottom;
      var sheet = purchaseSheet();
      if (sheet) {
        var sheetTop = sheet.getBoundingClientRect().top;
        if (sheetTop < limitBottom) limitBottom = sheetTop;
      }
      var maxTop = limitBottom - height;
      if (pos.top > maxTop) pos.top = maxTop;
    } else {
      var minLeft = stripRect.left;
      var maxLeft = stripRect.right - width;
      if (minLeft > maxLeft) {
        pos.left = minLeft;
      } else {
        pos.left = Math.min(Math.max(pos.left, minLeft), maxLeft);
      }
    }

    return pos;
  }

  /*
    Parked notes are absolute inside the image strip, not position:static.
    Static would snap the note back to the zero-height anchor. Absolute keeps
    the place it already occupied and lets it scroll away with the photos.
  */
  function parkNote(els) {
    var strip = imageStrip(els.image);
    if (!strip) return;

    var imgRect = els.image.getBoundingClientRect();
    var stripRect = strip.getBoundingClientRect();
    var left = imgRect.left - stripRect.left;
    var top = imgRect.top - stripRect.top;
    var maxTop = stripRect.height - imgRect.height;

    if (maxTop < 0) {
      top = maxTop;
    } else {
      if (top < 0) top = 0;
      if (top > maxTop) top = maxTop;
    }

    if (!narrowQuery.matches) {
      var maxLeft = stripRect.width - imgRect.width;
      if (maxLeft < 0) {
        left = 0;
      } else {
        if (left < 0) left = 0;
        if (left > maxLeft) left = maxLeft;
      }
    }

    els.image.style.position = "absolute";
    els.image.style.left = left + "px";
    els.image.style.top = top + "px";
    els.image.setAttribute("data-parked", "");
    els.image.setAttribute("data-positioned", "");
  }

  function unparkNote(els) {
    var rect = els.image.getBoundingClientRect();
    els.image.style.position = "";
    els.image.removeAttribute("data-parked");
    applyImagePos(els.image, rect.left, rect.top);
  }

  function followPosFromUnconstrained(els, unconstrained, width, height) {
    var left = unconstrained.left;
    var top = unconstrained.top;

    if (narrowQuery.matches) {
      top = Math.max(top, navOffsetPx() + peekPx() - height);
    } else {
      left = Math.max(left, peekPx() - width);
    }

    return clampToImageArea(els, left, top, width, height);
  }

  function followPos(els, ox, oy) {
    var width = els.image.offsetWidth;
    var height = els.image.offsetHeight;
    if (!width || !height) return null;

    var unconstrained = unconstrainedPos(els, width, height);
    return followPosFromUnconstrained(
      els,
      { left: unconstrained.left + ox, top: unconstrained.top + oy },
      width,
      height,
    );
  }

  var parkedWidth = 0;

  function syncHandwrittenPeek() {
    if (isDragging || isResetting) return;

    var els = handwrittenEls();
    if (!els.anchor || !els.image) return;

    if (mobileQuery.matches && !narrowQuery.matches && !offsetX && !offsetY) {
      clearHandwrittenInline(els.image);
      parkedWidth = 0;
      return;
    }

    if (shouldPark(els)) {
      if (
        els.image.hasAttribute("data-parked") &&
        parkedWidth !== window.innerWidth
      ) {
        els.image.style.position = "";
        els.image.style.left = "";
        els.image.style.top = "";
        els.image.removeAttribute("data-parked");
        els.image.removeAttribute("data-positioned");
      }

      if (!els.image.hasAttribute("data-parked")) {
        if (!els.image.hasAttribute("data-positioned")) {
          var initial = followPos(els, offsetX, offsetY);
          if (!initial) return;
          applyImagePos(els.image, initial.left, initial.top);
        }
        parkNote(els);
        parkedWidth = window.innerWidth;
      }
      return;
    }

    parkedWidth = 0;

    if (els.image.hasAttribute("data-parked")) {
      els.image.style.position = "";
      els.image.removeAttribute("data-parked");
    }

    var pos = followPos(els, offsetX, offsetY);
    if (!pos) return;

    applyImagePos(els.image, pos.left, pos.top);
  }

  function scheduleHandwrittenPeek() {
    if (peekFrame) return;
    peekFrame = requestAnimationFrame(function () {
      peekFrame = 0;
      syncHandwrittenPeek();
    });
  }

  function atPageStart() {
    return Math.abs(window.scrollX) < 1 && Math.abs(window.scrollY) < 1;
  }

  function hasOffset() {
    return offsetX !== 0 || offsetY !== 0;
  }

  function cancelRestore() {
    pendingRestore = false;
    clearRestoreScrollListeners();
    cancelPositionRestore();
  }

  function cancelPositionRestore() {
    if (!isResetting) return;

    isResetting = false;
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = 0;
    }
    var els = handwrittenEls();
    if (els.image) {
      els.image.removeAttribute("data-resetting");
      if (restoreFinish) {
        els.image.removeEventListener("transitionend", restoreFinish);
      }
    }
    restoreFinish = null;
  }

  function finishPositionRestore() {
    if (!isResetting) return;

    isResetting = false;
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = 0;
    }
    var els = handwrittenEls();
    if (els.image) {
      els.image.removeAttribute("data-resetting");
      if (restoreFinish) {
        els.image.removeEventListener("transitionend", restoreFinish);
      }
    }
    restoreFinish = null;
    syncHandwrittenPeek();
  }

  function startPositionRestore() {
    var els = handwrittenEls();
    if (!els.image || !els.anchor || !hasOffset()) return;

    cancelPositionRestore();

    var home = followPos(els, 0, 0);
    var current = els.image.getBoundingClientRect();
    var atHome =
      home &&
      Math.abs(current.left - home.left) < 1 &&
      Math.abs(current.top - home.top) < 1;

    offsetX = 0;
    offsetY = 0;

    if (atHome || !home) {
      syncHandwrittenPeek();
      return;
    }

    isResetting = true;
    els.image.setAttribute("data-resetting", "");
    els.image.getBoundingClientRect();
    applyImagePos(els.image, home.left, home.top);

    restoreFinish = function (e) {
      if (e.target !== els.image) return;
      if (e.propertyName !== "left" && e.propertyName !== "top") return;
      els.image.removeEventListener("transitionend", restoreFinish);
      restoreFinish = null;
      finishPositionRestore();
    };
    els.image.addEventListener("transitionend", restoreFinish);
    restoreTimer = setTimeout(finishPositionRestore, RESET_MS + 200);
  }

  function clearRestoreScrollListeners() {
    if (restoreScrollTimer) {
      clearTimeout(restoreScrollTimer);
      restoreScrollTimer = 0;
    }
    if (restoreFallbackTimer) {
      clearTimeout(restoreFallbackTimer);
      restoreFallbackTimer = 0;
    }
    window.removeEventListener("scroll", onRestoreScrollSettle);
    window.removeEventListener("scrollend", onRestoreScrollEnd);
  }

  function onRestoreScrollSettle() {
    if (!pendingRestore) return;
    if (restoreScrollTimer) clearTimeout(restoreScrollTimer);
    restoreScrollTimer = setTimeout(function () {
      restoreScrollTimer = 0;
      if (!pendingRestore) return;
      pendingRestore = false;
      clearRestoreScrollListeners();
      if (atPageStart()) startPositionRestore();
    }, 120);
  }

  function onRestoreScrollEnd() {
    if (!pendingRestore) return;
    pendingRestore = false;
    clearRestoreScrollListeners();
    if (atPageStart()) startPositionRestore();
  }

  function scrollHomeThenRestore() {
    cancelRestore();

    if (atPageStart()) {
      startPositionRestore();
      return;
    }

    if (hasOffset()) {
      pendingRestore = true;
      window.addEventListener("scroll", onRestoreScrollSettle, {
        passive: true,
      });
      window.addEventListener("scrollend", onRestoreScrollEnd, {
        passive: true,
      });
      restoreFallbackTimer = setTimeout(function () {
        restoreFallbackTimer = 0;
        if (!pendingRestore) return;
        pendingRestore = false;
        clearRestoreScrollListeners();
        if (atPageStart()) startPositionRestore();
      }, 1500);
    }

    window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  }

  function onDragPointerDown(e) {
    if (e.button != null && e.button !== 0) return;

    var els = handwrittenEls();
    if (!els.image) return;

    if (els.image.hasAttribute("data-parked")) unparkNote(els);

    var rect = els.image.getBoundingClientRect();
    var rebaseOffset = isResetting;
    if (isResetting) {
      applyImagePos(els.image, rect.left, rect.top);
    }
    cancelRestore();
    if (rebaseOffset && els.anchor) {
      var unconstrained = unconstrainedPos(
        els,
        els.image.offsetWidth,
        els.image.offsetHeight,
      );
      offsetX = rect.left - unconstrained.left;
      offsetY = rect.top - unconstrained.top;
    }

    isDragging = true;
    dragMoved = false;
    activePointerId = e.pointerId;
    dragStartClientX = e.clientX;
    dragStartClientY = e.clientY;
    dragStartLeft = rect.left;
    dragStartTop = rect.top;

    els.image.setPointerCapture(e.pointerId);
    e.preventDefault();

    els.image.addEventListener("pointermove", onDragPointerMove);
    els.image.addEventListener("pointerup", onDragPointerUp);
    els.image.addEventListener("pointercancel", onDragPointerUp);
  }

  function onDragPointerMove(e) {
    if (!isDragging || e.pointerId !== activePointerId) return;

    var els = handwrittenEls();
    if (!els.image) return;

    var dx = e.clientX - dragStartClientX;
    var dy = e.clientY - dragStartClientY;

    if (
      !dragMoved &&
      dx * dx + dy * dy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX
    ) {
      dragMoved = true;
      els.image.setAttribute("data-dragging", "");
    }

    if (!dragMoved) return;

    var width = els.image.offsetWidth;
    var height = els.image.offsetHeight;
    var pos = clampToImageArea(
      els,
      dragStartLeft + dx,
      dragStartTop + dy,
      width,
      height,
    );
    applyImagePos(els.image, pos.left, pos.top);
  }

  function onDragPointerUp(e) {
    if (e.pointerId !== activePointerId) return;

    var els = handwrittenEls();
    var image = els.image;
    var isCancel = e.type === "pointercancel";

    if (image) {
      image.removeEventListener("pointermove", onDragPointerMove);
      image.removeEventListener("pointerup", onDragPointerUp);
      image.removeEventListener("pointercancel", onDragPointerUp);
      image.removeAttribute("data-dragging");
      try {
        image.releasePointerCapture(e.pointerId);
      } catch (err) {}
    }

    var didMove = dragMoved;
    isDragging = false;
    dragMoved = false;
    activePointerId = null;

    if (!image) return;

    if (!didMove) {
      if (!isCancel) scrollHomeThenRestore();
      return;
    }

    if (!els.anchor) return;

    var width = image.offsetWidth;
    var height = image.offsetHeight;
    var unconstrained = unconstrainedPos(els, width, height);
    var rect = image.getBoundingClientRect();
    var pos = followPosFromUnconstrained(
      els,
      { left: rect.left, top: rect.top },
      width,
      height,
    );
    offsetX = pos.left - unconstrained.left;
    offsetY = pos.top - unconstrained.top;
    applyImagePos(image, pos.left, pos.top);
    if (shouldPark(els)) {
      parkNote(els);
      parkedWidth = window.innerWidth;
    }
  }

  function bindHandwrittenDrag(image) {
    image.addEventListener("pointerdown", onDragPointerDown);
    image.addEventListener("click", function (e) {
      e.preventDefault();
    });
    image.addEventListener("dragstart", function (e) {
      e.preventDefault();
    });
  }

  /* --- init -------------------------------------------------------------- */

  syncHandwrittenPeek();
  syncScrollConverter();
  scheduleHandwrittenPeek();

  window.addEventListener("scroll", scheduleHandwrittenPeek, { passive: true });
  window.addEventListener("resize", scheduleHandwrittenPeek);

  var els = handwrittenEls();
  if (els.image) {
    bindHandwrittenDrag(els.image);

    var img = els.image.querySelector("img");
    if (img && !img.complete) {
      img.addEventListener("load", scheduleHandwrittenPeek, { once: true });
    }
  }

  function onViewportModeChange() {
    var current = handwrittenEls();
    cancelRestore();
    offsetX = 0;
    offsetY = 0;
    if (current.image) clearHandwrittenInline(current.image);
    syncScrollConverter();
    scheduleHandwrittenPeek();
  }

  if (typeof mobileQuery.addEventListener === "function") {
    mobileQuery.addEventListener("change", onViewportModeChange);
    narrowQuery.addEventListener("change", onViewportModeChange);
  } else if (typeof mobileQuery.addListener === "function") {
    mobileQuery.addListener(onViewportModeChange);
    narrowQuery.addListener(onViewportModeChange);
  }
})();
